import { useCallback, useRef, useState } from 'react'
import {
    findShopPayFit,
    getInnerWidth,
    getShopPayLogoWidth,
    SHOP_PAY_DEFAULT_FONT_SIZE,
    SHOP_PAY_DEFAULT_LOGO_HEIGHT,
    SHOP_PAY_MIN_FONT_SIZE,
    SHOP_PAY_MIN_LOGO_HEIGHT,
    type ShopPayFitResult,
} from '../utils/fitToWidth'
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect'
import { useObservedLayoutMeasure } from './useObservedLayoutMeasure'

type UseShopPayFitToWidthOptions = {
    defaultFontSize?: number
    minFontSize?: number
    defaultLogoHeight?: number
    minLogoHeight?: number
    enabled?: boolean
    variant?: 'buy' | 'checkout'
}

const DEFAULT_FIT: ShopPayFitResult = {
    fontSize: SHOP_PAY_DEFAULT_FONT_SIZE,
    logoHeight: SHOP_PAY_DEFAULT_LOGO_HEIGHT,
    logoOnly: false,
}

function applyShopPayFitStyles(
    container: HTMLDivElement,
    prefix: HTMLSpanElement,
    logo: SVGSVGElement,
    fit: ShopPayFitResult,
) {
    container.style.fontSize = `${fit.fontSize}px`
    prefix.style.display = fit.logoOnly ? 'none' : ''
    logo.style.height = `${fit.logoHeight}px`
    logo.style.width = `${getShopPayLogoWidth(fit.logoHeight)}px`
}

function clearShopPayFitStyles(
    container: HTMLDivElement,
    prefix?: HTMLSpanElement | null,
    logo?: SVGSVGElement | null,
) {
    container.style.fontSize = ''
    if (prefix) {
        prefix.style.display = ''
    }
    if (logo) {
        logo.style.height = ''
        logo.style.width = ''
    }
}

export type ShopPayFitToWidthResult = {
    containerRef: React.RefObject<HTMLDivElement>
    contentRef: React.RefObject<HTMLSpanElement>
    prefixRef: React.RefObject<HTMLSpanElement>
    logoRef: React.RefObject<SVGSVGElement>
    fit: ShopPayFitResult
}

export function useShopPayFitToWidth(
    options: UseShopPayFitToWidthOptions = {},
): ShopPayFitToWidthResult {
    const {
        defaultFontSize = SHOP_PAY_DEFAULT_FONT_SIZE,
        minFontSize = SHOP_PAY_MIN_FONT_SIZE,
        defaultLogoHeight = SHOP_PAY_DEFAULT_LOGO_HEIGHT,
        minLogoHeight = SHOP_PAY_MIN_LOGO_HEIGHT,
        enabled = true,
        variant = 'buy',
    } = options

    const containerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLSpanElement>(null)
    const prefixRef = useRef<HTMLSpanElement>(null)
    const logoRef = useRef<SVGSVGElement>(null)
    const [fit, setFit] = useState<ShopPayFitResult>(DEFAULT_FIT)

    const measure = useCallback(() => {
        const container = containerRef.current
        const content = contentRef.current
        const prefix = prefixRef.current
        const logo = logoRef.current
        if (!enabled || !container || !content || !prefix || !logo) {
            return
        }

        const innerWidth = getInnerWidth(container)
        if (innerWidth <= 0) {
            return
        }

        const nextFit = findShopPayFit({
            innerWidth,
            defaultFontSize,
            minFontSize,
            defaultLogoHeight,
            minLogoHeight,
            measure: (state) => {
                applyShopPayFitStyles(container, prefix, logo, state)
                return content.scrollWidth
            },
        })

        applyShopPayFitStyles(container, prefix, logo, nextFit)
        setFit((prev) =>
            prev.fontSize === nextFit.fontSize &&
            prev.logoHeight === nextFit.logoHeight &&
            prev.logoOnly === nextFit.logoOnly
                ? prev
                : nextFit,
        )
    }, [
        defaultFontSize,
        defaultLogoHeight,
        enabled,
        minFontSize,
        minLogoHeight,
    ])

    useIsomorphicLayoutEffect(() => {
        if (!enabled) {
            const container = containerRef.current
            if (container) {
                clearShopPayFitStyles(
                    container,
                    prefixRef.current,
                    logoRef.current,
                )
            }
            setFit(DEFAULT_FIT)
        }
    }, [enabled])

    useObservedLayoutMeasure({
        containerRef,
        contentRef,
        depKey: variant,
        enabled,
        measure,
    })

    return { containerRef, contentRef, prefixRef, logoRef, fit }
}
